import { useMemo } from 'react';
import ReactTooltip from 'rc-tooltip';

import TooltipIcon from 'images/icons/tooltip.inline.svg';

import { TooltipContainer } from './Tooltip.styled';

import type { ReactNode, FC } from 'react';

export type TooltipDirection = 'left' | 'right' | 'top' | 'bottom';

export interface TooltipProps {
  /** Content to appear in the tooltip popup */
  overlay: ReactNode;
  /** Direction popup appears in */
  direction?: TooltipDirection;
  /**
   * Component that displays tooltip popup on hover
   * - if this is not provided, we just use a tooltip icon
   */
  children?: ReactNode;
}

/**
 * This returns an [offsetX, and offsetY] value that will offset the position of
 * the tooltip popup - see rc-tooltip's API for more details (`align` prop).
 * WIthout this the popup looks too close to the element it's attached to!
 */
export const calculateOffset = (direction: TooltipDirection): number[] => {
  const offset = 24;

  switch (direction) {
    case 'right':
      return [offset, 0];
    case 'left':
      return [-offset, 0];
    case 'top':
      return [0, -offset];
    case 'bottom':
      return [0, offset];
    default:
      return [];
  }
};

export const Tooltip: FC<TooltipProps> = ({ overlay, children, direction = 'right' }) => {
  const align = useMemo(
    () => ({
      offset: calculateOffset(direction),
    }),
    [direction]
  );

  return (
    <ReactTooltip placement={direction} overlay={overlay} align={align}>
      {/* children must a span or a div (or any other built-in component),
        so we enforce this @see: https://www.npmjs.com/package/rc-tooltip#important-note */}
      <TooltipContainer aria-label="Tooltip">{children || <TooltipIcon />}</TooltipContainer>
    </ReactTooltip>
  );
};
